import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useObserver } from 'mobx-react';
import { useTheme } from '@mui/material/styles';

import { Button } from '@shared/components/Button';
import { ConfirmationModal } from '@shared/components/ConfirmationModal';
import { useDevicePairingUIStore } from '@core/useStores';

import { useStyles } from './PairedDevicesView.styles';
import { messages } from 'Profile/profile.messages';
import { ThemeProps } from '@styles/theme';

/**
 * Devices signed in by pairing a code rather than a password.
 * A paired device stays signed in indefinitely — that is the point of the flow — so this list and
 * its revoke action are what keeps that from being a one-way door.
 */
export const PairedDevicesView: React.FC = () => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const devicePairingUIStore = useDevicePairingUIStore();
    const [deviceToRevoke, setDeviceToRevoke] = useState<string | null>(null);

    useEffect(() => {
        devicePairingUIStore.getDevices();
    }, []);

    const revoke = () => {
        if (deviceToRevoke) {
            devicePairingUIStore.revokeDevice(deviceToRevoke);
        }

        setDeviceToRevoke(null);
    };

    return useObserver(() => (
        <div className={styles.wrapper}>
            <h4 className={styles.heading}>{messages['pairedDevices.title']}</h4>
            <p className={styles.subtitle}>{messages['pairedDevices.subtitle']}</p>

            {
                devicePairingUIStore.devices.length === 0
                    ? <p className={styles.empty}>{messages['pairedDevices.empty']}</p>
                    : devicePairingUIStore.devices.map(device => (
                        <div className={styles.device} key={device.id}>
                            <div>
                                <div className={styles.deviceName}>
                                    {device.deviceName || messages['pairedDevices.unnamed']}
                                </div>
                                <div className={styles.deviceMeta}>
                                    {
                                        messages['pairedDevices.meta'](
                                            device.devicePlatform,
                                            moment(device.pairedAt).format('LLL'),
                                            device.pairedFromIp
                                        )
                                    }
                                </div>
                            </div>

                            <Button
                                color="error"
                                onClick={() => setDeviceToRevoke(device.id)}
                                disabled={devicePairingUIStore.devicesStatus.isLoading}
                            >
                                {messages['pairedDevices.revoke']}
                            </Button>
                        </div>
                    ))
            }

            <ConfirmationModal
                isOpen={!!deviceToRevoke}
                message={messages['pairedDevices.revokeConfirm.message']}
                note={messages['pairedDevices.revokeConfirm.note']}
                confirmText={messages['pairedDevices.revoke']}
                onDecline={() => setDeviceToRevoke(null)}
                onConfirm={revoke}
            />
        </div>
    ));
}
